'use client'

import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-full font-medium transition-colors',
  {
    variants: {
      variant: {
        default: 'bg-[var(--color-surface-hover)] text-[var(--color-text-secondary)]',
        success: 'bg-[var(--color-green-muted)] text-[var(--color-green)]',
        warning: 'bg-[var(--color-orange-muted)] text-[var(--color-orange)]',
        danger: 'bg-[var(--color-pink-muted)] text-[var(--color-pink)]',
        info: 'bg-[var(--color-cyan-muted)] text-[var(--color-cyan)]',
        lime: 'bg-[var(--color-lime-muted)] text-[var(--color-lime)]',
        cyan: 'bg-[var(--color-cyan-muted)] text-[var(--color-cyan)]',
        pink: 'bg-[var(--color-pink-muted)] text-[var(--color-pink)]',
        purple: 'bg-[var(--color-purple-muted)] text-[var(--color-purple)]',
      },
      size: {
        sm: 'px-1.5 py-0.5 text-[10px]',
        default: 'px-2.5 py-0.5 text-xs',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

const dotColorMap = {
  default: 'bg-[var(--color-text-muted)]',
  success: 'bg-[var(--color-green)]',
  warning: 'bg-[var(--color-orange)]',
  danger: 'bg-[var(--color-pink)]',
  info: 'bg-[var(--color-cyan)]',
  lime: 'bg-[var(--color-lime)]',
  cyan: 'bg-[var(--color-cyan)]',
  pink: 'bg-[var(--color-pink)]',
  purple: 'bg-[var(--color-purple)]',
} as const

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  dot?: boolean
  removable?: boolean
  onRemove?: () => void
}

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, size, dot, removable, onRemove, children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(badgeVariants({ variant, size }), className)}
        {...props}
      >
        {dot && (
          <span className={cn('w-1.5 h-1.5 rounded-full', dotColorMap[variant || 'default'])} />
        )}
        {children}
        {removable && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onRemove?.()
            }}
            className="ml-0.5 hover:opacity-70 transition-opacity"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </span>
    )
  }
)
Badge.displayName = 'Badge'

export { Badge, badgeVariants }
